import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Menu,
  MenuItem,
  IconButton,
  Paper,
  Avatar,
  Stack,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import QuotationConvertAddressDialog from "./QuotationConvertAddressDialog";
import {
  amountToWords,
  formatMoney,
  formatQuotationLabel,
  QUOTATION_STATUSES,
} from "../utils/quotationUtils";
import { PURPLE, PURPLE_DARK } from "./leadConstants";

const INK = "#0f172a";
const MUTED = "#64748b";
const LINE = "#e8edf3";
const SECTION_X = { xs: 2, sm: 3.5 };

function formatDisplayDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function statusMeta(status) {
  return QUOTATION_STATUSES.find((s) => s.value === status) || { label: status, color: "default" };
}

function getInitials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.[0] || "?").toUpperCase();
}

function SectionLabel({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: MUTED,
        mb: 0.75,
        fontSize: "0.68rem",
      }}
    >
      {children}
    </Typography>
  );
}

function TotalsRow({ label, value, negative = false, grand = false }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        py: grand ? 0 : 0.65,
        px: grand ? 2 : 0,
        ...(grand
          ? {
            mx: -2,
            mt: 1.25,
            py: 1.25,
            bgcolor: PURPLE_DARK,
            color: "#fff",
            borderRadius: 1,
          }
          : {}),
      }}
    >
      <Typography
        variant={grand ? "subtitle2" : "body2"}
        fontWeight={grand ? 700 : 500}
        color={grand ? "inherit" : MUTED}
        sx={{ fontSize: grand ? "0.8rem" : "0.8125rem" }}
      >
        {label}
      </Typography>
      <Typography
        variant={grand ? "subtitle1" : "body2"}
        fontWeight={700}
        color={grand ? "inherit" : negative ? "error.main" : INK}
        sx={{ fontSize: grand ? "1rem" : "0.8125rem" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function chargeMinor(subtotalMinor, charge) {
  if (!charge) return 0;
  if (charge.type === "rate") {
    return Math.round(subtotalMinor * (Number(charge.value) || 0) / 100);
  }
  return charge.value_minor || 0;
}

function adjustmentLabel(title, adjustment) {
  if (!adjustment?.type) return title;
  return `${title} (${adjustment.type})`;
}

export default function QuotationDetailDialog({
  open,
  quotation,
  onClose,
  onEdit,
  onDelete,
  onStatusChange,
  onDownloadPdf,
  onSendWhatsApp,
  onConvertToOrder,
  onConvertToInvoice,
  downloading = false,
  sending = false,
  converting = false,
  invoicing = false,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [convertAddressOpen, setConvertAddressOpen] = useState(false);

  if (!quotation) return null;

  const status = statusMeta(quotation.status);
  const adjustments = quotation.adjustments || {};
  const lineItems = quotation.line_items || [];
  const preparedBy = quotation.created_by_account_name || "Team member";
  const customerName = quotation.contact_name || quotation.lead_title || "Customer";
  const taxableBaseMinor = Math.max(
    0,
    (quotation.subtotal_minor || 0) - (quotation.discount_minor || 0) + (quotation.charges_minor || 0),
  );

  const thSx = {
    fontWeight: 600,
    color: MUTED,
    fontSize: "0.72rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${LINE}`,
    py: 1,
    px: 2,
    bgcolor: "#f8fafc",
  };

  const tdSx = {
    borderBottom: `1px solid ${LINE}`,
    py: 1.25,
    px: 2,
    verticalAlign: "middle",
    fontSize: "0.8125rem",
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      disableEnforceFocus={convertAddressOpen}
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          overflow: "hidden",
          maxHeight: "92vh",
          boxShadow: "0 24px 48px rgba(15,23,42,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2.5,
          bgcolor: "#fff",
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700} color={INK}>
            {formatQuotationLabel(quotation.quotation_number)}
          </Typography>
          <Typography variant="caption" color={MUTED}>
            Quotation preview
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            size="small"
            label={status.label}
            color={status.color}
            sx={{ fontWeight: 600, height: 26 }}
          />
          <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ color: MUTED }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: "#f4f6f9" }}>
        <Paper
          elevation={0}
          sx={{
            m: { xs: 1.5, sm: 2.5 },
            border: `1px solid ${LINE}`,
            borderRadius: 2,
            bgcolor: "#fff",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
          }}
        >
          {/* Top accent */}
          <Box sx={{ height: 4, background: `linear-gradient(90deg, ${PURPLE} 0%, ${PURPLE_DARK} 100%)` }} />

          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              flexWrap: "wrap",
              px: SECTION_X,
              pt: 2.5,
              pb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                sx={{
                  bgcolor: PURPLE,
                  width: 42,
                  height: 42,
                  borderRadius: 1.5,
                  fontWeight: 700,
                  fontSize: 15,
                  boxShadow: `0 4px 12px ${PURPLE}40`,
                }}
              >
                {getInitials(preparedBy)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} color={INK} lineHeight={1.2}>
                  {preparedBy}
                </Typography>
                <Typography variant="caption" color={MUTED}>
                  Prepared by
                </Typography>
              </Box>
            </Box>

            <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
              <Typography
                sx={{
                  fontSize: "1.35rem",
                  fontWeight: 800,
                  letterSpacing: "0.12em",
                  color: PURPLE_DARK,
                  lineHeight: 1,
                  mb: 1,
                }}
              >
                QUOTATION
              </Typography>
              <Typography variant="body2" color={MUTED} sx={{ lineHeight: 1.8 }}>
                No.
                {" "}
                <Box component="span" fontWeight={700} color={INK}>
                  {formatQuotationLabel(quotation.quotation_number)}
                </Box>
              </Typography>
              <Typography variant="body2" color={MUTED} sx={{ lineHeight: 1.8 }}>
                Date
                {" "}
                <Box component="span" fontWeight={600} color={INK}>
                  {formatDisplayDate(quotation.quotation_date)}
                </Box>
              </Typography>
            </Box>
          </Box>

          {/* Customer */}
          <Box
            sx={{
              mx: SECTION_X,
              mb: 2,
              px: 2,
              py: 1.5,
              borderRadius: 1.5,
              bgcolor: "#f5f3ff",
              borderLeft: `4px solid ${PURPLE}`,
            }}
          >
            <SectionLabel>Quotation for</SectionLabel>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
              <Typography variant="body1" fontWeight={700} color={INK}>
                {customerName}
              </Typography>
              {quotation.contact_phone && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 10,
                    bgcolor: "#fff",
                    border: `1px solid ${LINE}`,
                  }}
                >
                  <PhoneIcon sx={{ fontSize: 14, color: PURPLE }} />
                  <Typography variant="body2" fontWeight={600} color={INK}>
                    {quotation.contact_phone}
                  </Typography>
                </Box>
              )}
              {quotation.lead_title && (
                <Typography variant="caption" color={MUTED}>
                  Lead:
                  {" "}
                  {quotation.lead_title}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Table */}
          <Box sx={{ overflowX: "auto", borderTop: `1px solid ${LINE}` }}>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell width={36} sx={thSx}>#</TableCell>
                  <TableCell sx={thSx}>Item</TableCell>
                  <TableCell align="right" sx={thSx} width={100}>Price</TableCell>
                  <TableCell align="center" sx={thSx} width={48}>Qty</TableCell>
                  <TableCell align="right" sx={thSx} width={100}>Amount</TableCell>
                  <TableCell align="right" sx={thSx} width={88}>GST</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item, index) => {
                  const lineWithTax = (item.line_total_minor || 0) + (item.tax_amount_minor || 0);
                  return (
                    <TableRow
                      key={item.id || index}
                      sx={{ "&:last-child td": { borderBottom: 0 }, "&:hover": { bgcolor: "#fafbfc" } }}
                    >
                      <TableCell sx={{ ...tdSx, color: MUTED, fontWeight: 600 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 1,
                              overflow: "hidden",
                              bgcolor: "#f1f5f9",
                              border: `1px solid ${LINE}`,
                              flexShrink: 0,
                            }}
                          >
                            {item.image_url ? (
                              <Box
                                component="img"
                                src={item.image_url}
                                alt={item.name}
                                sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: MUTED,
                                  fontWeight: 700,
                                  fontSize: 13,
                                }}
                              >
                                {getInitials(item.name)}
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} color={INK} noWrap>
                              {item.name}
                            </Typography>
                            {item.description && (
                              <Typography variant="caption" color={MUTED} display="block" noWrap>
                                {item.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ ...tdSx, fontWeight: 600, color: INK }}>
                        {formatMoney(item.unit_price_minor, quotation.currency)}
                      </TableCell>
                      <TableCell align="center" sx={{ ...tdSx, fontWeight: 600, color: INK }}>
                        {item.quantity}
                      </TableCell>
                      <TableCell align="right" sx={{ ...tdSx, fontWeight: 700, color: INK }}>
                        {formatMoney(item.line_total_minor, quotation.currency)}
                      </TableCell>
                      <TableCell align="right" sx={tdSx}>
                        {item.tax_rate ? (
                          <Box>
                            <Typography variant="body2" fontWeight={600} color={INK}>
                              {formatMoney(item.tax_amount_minor, quotation.currency)}
                            </Typography>
                            <Typography variant="caption" color={MUTED}>
                              {item.tax_rate}
                              % ·
                              {" "}
                              {formatMoney(lineWithTax, quotation.currency)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 260px" },
              gap: 0,
              borderTop: `1px solid ${LINE}`,
              bgcolor: "#fafbfc",
            }}
          >
            <Box sx={{ px: SECTION_X, py: 2.5, borderRight: { sm: `1px solid ${LINE}` } }}>
              <SectionLabel>Amount in words</SectionLabel>
              <Typography
                variant="body2"
                fontWeight={600}
                color={INK}
                sx={{
                  mb: 2,
                  lineHeight: 1.6,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  bgcolor: "#fff",
                  border: `1px solid ${LINE}`,
                }}
              >
                {amountToWords(quotation.total_minor, quotation.currency)}
              </Typography>

              <SectionLabel>Notes</SectionLabel>
              <Typography
                variant="body2"
                color={quotation.notes ? INK : MUTED}
                sx={{
                  minHeight: 48,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  bgcolor: "#fff",
                  border: `1px dashed ${LINE}`,
                  whiteSpace: "pre-wrap",
                  fontStyle: quotation.notes ? "normal" : "italic",
                }}
              >
                {quotation.notes || "No notes added"}
              </Typography>

              {(quotation.converted_order_ref || quotation.invoice_ref) && (
                <Stack direction="row" spacing={0.75} sx={{ mt: 1.5, flexWrap: "wrap", gap: 0.75 }}>
                  {quotation.converted_order_ref && (
                    <Chip size="small" label={`Order ${quotation.converted_order_ref}`} variant="outlined" />
                  )}
                  {quotation.invoice_ref && (
                    <Chip size="small" label={`Invoice ${quotation.invoice_ref}`} variant="outlined" color="primary" />
                  )}
                </Stack>
              )}
            </Box>

            <Box sx={{ px: 2, py: 2.5 }}>
              <TotalsRow label="Subtotal" value={formatMoney(quotation.subtotal_minor, quotation.currency)} />
              {quotation.discount_minor > 0 && (
                <TotalsRow
                  label={adjustmentLabel("Discount", adjustments.discount)}
                  value={`-${formatMoney(quotation.discount_minor, quotation.currency)}`}
                  negative
                />
              )}
              {(adjustments.charges || []).map((charge, index) => (
                <TotalsRow
                  key={index}
                  label={adjustmentLabel(charge.title || "Charge", charge)}
                  value={formatMoney(chargeMinor(quotation.subtotal_minor, charge), quotation.currency)}
                />
              ))}
              {quotation.tax_minor > 0 && (
                <TotalsRow label="Product GST" value={formatMoney(quotation.tax_minor, quotation.currency)} />
              )}
              <TotalsRow label="Total" value={formatMoney(taxableBaseMinor, quotation.currency)} />
              <TotalsRow
                label="Grand total (incl. GST)"
                value={formatMoney(quotation.total_minor, quotation.currency)}
                grand
              />
            </Box>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: "#fff",
          borderTop: `1px solid ${LINE}`,
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {status.label}
          </Button>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" }, mx: 0.5 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => onDownloadPdf?.(quotation)}
            disabled={downloading}
            sx={{ textTransform: "none" }}
          >
            {downloading ? "…" : "PDF"}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => onSendWhatsApp?.(quotation)}
            disabled={sending || !quotation.contact_phone}
            sx={{ textTransform: "none", bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DARK } }}
          >
            {sending ? "…" : "Send"}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ShoppingCartCheckoutIcon />}
            onClick={() => setConvertAddressOpen(true)}
            disabled={converting || !quotation.contact_phone}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {converting ? "…" : quotation.status === "converted" ? "Re-convert" : "Convert to order"}
          </Button>
        </Stack>

        <Stack direction="row" spacing={0.75} alignItems="center">
          <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => onEdit?.(quotation)} sx={{ textTransform: "none" }}>
            Edit
          </Button>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => onDelete?.(quotation)}
            sx={{ textTransform: "none" }}
          >
            Delete
          </Button>
          <Button size="small" variant="outlined" onClick={onClose} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </Stack>
      </DialogActions>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {QUOTATION_STATUSES.map((item) => (
          <MenuItem
            key={item.value}
            selected={quotation.status === item.value}
            onClick={() => {
              setMenuAnchor(null);
              onStatusChange?.(quotation, item.value);
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      <QuotationConvertAddressDialog
        open={convertAddressOpen}
        quotation={quotation}
        submitting={converting}
        onClose={() => {
          if (converting) return;
          setConvertAddressOpen(false);
        }}
        onConfirm={async (deliveryAddress) => {
          try {
            await onConvertToOrder?.(quotation, deliveryAddress);
            setConvertAddressOpen(false);
          } catch {
            // Parent shows error feedback.
          }
        }}
      />

    </Dialog>
  );
}
